// Galería del hero con miniaturas y visor ampliado.
// Componente pdp-*: el HTML llega renderizado desde Liquid y este módulo solo añade interacción.

const define = (name, element) => {
  if (!customElements.get(name)) customElements.define(name, element);
};
const lockScroll = (locked) => {
  document.documentElement.style.overflow = locked ? 'hidden' : '';
};

/* ---------- Galería con miniaturas y visor ---------- */
class PdpGallery extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-track]');
    this.slides = [...this.track.children];
    this.index = 0;
    this.dialog = this.querySelector('dialog');
    this.updateArrows();

    // Mouse drag on the gallery and the enlarged view. Loaded apart: if it fails, arrows and swipe still work.
    this.track.addEventListener('pointerdown', () => { this.dragStartIndex = this.index; });
    this.dialog?.querySelector('[data-lightbox-track]')?.addEventListener('pointerdown', () => {
      this.lightboxDragStart = this.lightboxIndex();
    });
    import('./pdp-drag.js')
      .then(({ enableDrag }) => {
        enableDrag(this.track, {
          onRelease: (direction) => {
            const nearest = Math.round(this.track.scrollLeft / Math.max(1, this.track.clientWidth));
            this.goTo(direction === 0 ? nearest : this.dragStartIndex + direction);
          },
        });
        if (this.lightboxTrack) {
          enableDrag(this.lightboxTrack, {
            onRelease: (direction) => {
              const total = this.lightboxTrack.children.length;
              const target = direction === 0 ? this.lightboxIndex() : this.lightboxDragStart + direction;
              const clamped = Math.max(0, Math.min(total - 1, target));
              this.lightboxTrack.scrollTo({ left: clamped * this.lightboxTrack.clientWidth, behavior: 'smooth' });
            },
          });
        }
      })
      .catch(() => {});

    this.querySelector('[data-prev]')?.addEventListener('click', () => this.goTo(this.index - 1));
    this.querySelector('[data-next]')?.addEventListener('click', () => this.goTo(this.index + 1));
    this.querySelectorAll('[data-thumb]').forEach((thumb) =>
      thumb.addEventListener('click', () => this.goTo(Number(thumb.dataset.thumb)))
    );
    this.track.addEventListener('scroll', () => requestAnimationFrame(() => this.syncIndex()), { passive: true });
    this.track.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowRight') this.goTo(this.index + 1);
      if (event.key === 'ArrowLeft') this.goTo(this.index - 1);
    });

    if (this.dialog) {
      this.lightboxTrack = this.dialog.querySelector('[data-lightbox-track]');
      this.querySelector('[data-zoom]')?.addEventListener('click', () => this.openLightbox());
      this.dialog.querySelector('[data-close]').addEventListener('click', () => this.dialog.close());
      this.dialog.addEventListener('close', () => {
        lockScroll(false);
        this.goTo(this.lightboxIndex(), 'instant');
        this.querySelector('[data-zoom]')?.focus();
      });
      this.lightboxTrack.addEventListener(
        'scroll',
        () => requestAnimationFrame(() => {
          this.dialog.querySelector('[data-lightbox-current]').textContent = this.lightboxIndex() + 1;
          this.updateArrows();
        }),
        { passive: true }
      );
      // Arrows and keyboard in the enlarged view: with a mouse, swiping is not an option.
      const lightboxGo = (step) => {
        const total = this.lightboxTrack.children.length;
        const target = Math.max(0, Math.min(total - 1, this.lightboxIndex() + step));
        this.lightboxTrack.scrollTo({ left: target * this.lightboxTrack.clientWidth, behavior: 'smooth' });
      };
      this.dialog.querySelector('[data-lightbox-prev]')?.addEventListener('click', () => lightboxGo(-1));
      this.dialog.querySelector('[data-lightbox-next]')?.addEventListener('click', () => lightboxGo(1));
      this.dialog.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') { event.preventDefault(); lightboxGo(-1); }
        if (event.key === 'ArrowRight') { event.preventDefault(); lightboxGo(1); }
      });
    }
  }

  goTo(index, behavior = 'smooth') {
    const target = Math.max(0, Math.min(this.slides.length - 1, index));
    this.track.scrollTo({ left: this.slides[target].offsetLeft, behavior });
  }

  syncIndex() {
    const index = Math.round(this.track.scrollLeft / this.track.clientWidth);
    if (index === this.index) return;
    this.index = index;
    const current = this.querySelector('[data-current]');
    if (current) current.textContent = index + 1;
    this.querySelectorAll('.pdp-gallery__dot').forEach((dot, position) => {
      dot.toggleAttribute('data-active', position === index);
    });
    this.querySelectorAll('[data-thumb]').forEach((thumb) => {
      thumb.setAttribute('aria-current', String(Number(thumb.dataset.thumb) === index));
    });
    this.querySelector(`[data-thumb="${index}"]`)?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    this.updateArrows();
  }

  // The first image has no "previous" arrow and the last one no "next" arrow.
  updateArrows() {
    const last = this.slides.length - 1;
    const prev = this.querySelector('[data-prev]');
    const next = this.querySelector('[data-next]');
    if (prev) prev.hidden = this.index <= 0;
    if (next) next.hidden = this.index >= last;
    if (this.lightboxTrack) {
      const lightboxIndex = this.lightboxIndex();
      const lightboxLast = this.lightboxTrack.children.length - 1;
      const lightboxPrev = this.dialog.querySelector('[data-lightbox-prev]');
      const lightboxNext = this.dialog.querySelector('[data-lightbox-next]');
      if (lightboxPrev) lightboxPrev.hidden = lightboxIndex <= 0;
      if (lightboxNext) lightboxNext.hidden = lightboxIndex >= lightboxLast;
    }
  }

  lightboxIndex() {
    return Math.round(this.lightboxTrack.scrollLeft / Math.max(1, this.lightboxTrack.clientWidth));
  }

  openLightbox() {
    this.dialog.showModal();
    lockScroll(true);
    this.lightboxTrack.scrollTo({ left: this.index * this.lightboxTrack.clientWidth, behavior: 'instant' });
    this.dialog.querySelector('[data-lightbox-current]').textContent = this.index + 1;
    this.updateArrows();
  }
}
define('pdp-gallery', PdpGallery);
